import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { REQUIRED_QUESTION_COUNT } from '../../shared/assessment';
import { normalizeStudentName, normalizeStudentPhone } from '../../shared/student-details';
import { useLanguage } from '../i18n/LanguageContext';
import {
  getQuestions,
  sendAssessmentResultToTeacher,
  submitAssessment,
  type AssessmentResult,
  type PublicQuestion,
} from '../services/assessment';

type TestStage = 'intro' | 'loading' | 'questions' | 'result';

export function TestPage() {
  const { locale, t } = useLanguage();
  const [stage, setStage] = useState<TestStage>('intro');
  const [questions, setQuestions] = useState<PublicQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [message, setMessage] = useState('');
  const [result, setResult] = useState<AssessmentResult | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [studentName, setStudentName] = useState('');
  const [studentPhone, setStudentPhone] = useState('');
  const [hasContactConsent, setHasContactConsent] = useState(false);
  const [isSendingResult, setIsSendingResult] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  const [wasResultSent, setWasResultSent] = useState(false);

  const currentQuestion = questions[currentIndex];
  const answerCount = Object.keys(answers).length;
  const progress = questions.length ? ((currentIndex + 1) / questions.length) * 100 : 0;

  useEffect(() => {
    if (stage === 'questions') document.querySelector<HTMLElement>('.question-card')?.focus();
  }, [currentIndex, stage]);

  const answerPayload = useMemo(
    () => questions.map((question) => ({ questionId: question.id, optionIndex: answers[question.id] ?? -1 })),
    [answers, questions],
  );

  async function startTest() {
    setStage('loading');
    setMessage('');
    try {
      const response = await getQuestions();
      setQuestions(response.questions);
      setStage('questions');
    } catch {
      setMessage(t.test.loadingError);
      setStage('intro');
    }
  }

  function chooseAnswer(optionIndex: number) {
    if (!currentQuestion) return;
    setAnswers((current) => ({ ...current, [currentQuestion.id]: optionIndex }));
    setMessage('');
  }

  function goNext() {
    if (!currentQuestion || answers[currentQuestion.id] === undefined) {
      setMessage(t.test.selectAnswer);
      return;
    }
    setMessage('');
    setCurrentIndex((index) => Math.min(index + 1, questions.length - 1));
  }

  async function submit() {
    if (answerCount !== REQUIRED_QUESTION_COUNT || answerPayload.some((answer) => answer.optionIndex < 0)) {
      setMessage(t.test.validationError);
      return;
    }

    setIsSubmitting(true);
    setMessage('');
    try {
      const nextResult = await submitAssessment(answerPayload, locale);
      setResult(nextResult);
      setStage('result');
    } catch {
      setMessage(t.test.serverError);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function sendResultToTeacher(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalizedName = normalizeStudentName(studentName);
    const normalizedPhone = normalizeStudentPhone(studentPhone);

    if (!normalizedName || !normalizedPhone || !hasContactConsent) {
      setShareMessage(t.test.detailsError);
      return;
    }

    setStudentName(normalizedName);
    setStudentPhone(normalizedPhone);
    setIsSendingResult(true);
    setShareMessage('');
    try {
      await sendAssessmentResultToTeacher(answerPayload, locale, {
        name: normalizedName,
        phone: normalizedPhone,
      });
      setWasResultSent(true);
    } catch {
      setShareMessage(t.test.shareError);
    } finally {
      setIsSendingResult(false);
    }
  }

  function reset() {
    setStage('intro');
    setQuestions([]);
    setAnswers({});
    setCurrentIndex(0);
    setResult(null);
    setMessage('');
    setStudentName('');
    setStudentPhone('');
    setHasContactConsent(false);
    setIsSendingResult(false);
    setShareMessage('');
    setWasResultSent(false);
  }

  return (
    <main className="test-page" id="main-content">
      <div className="test-page-glow" aria-hidden="true" />
      <div className="shell test-shell">
        {stage === 'intro' && (
          <section className="test-intro" aria-labelledby="test-title">
            <div className="test-monogram" aria-hidden="true">Q</div>
            <p className="eyebrow eyebrow-gold">{t.test.eyebrow}</p>
            <h1 id="test-title">{t.test.title}</h1>
            <p>{t.test.intro}</p>
            <div className="test-intro-meta">
              <span><strong>{REQUIRED_QUESTION_COUNT}</strong> Grammar + Vocabulary</span>
              <span><strong>≈ 12</strong> min</span>
              <span><strong>A1—C1</strong> CEFR</span>
            </div>
            <button className="button button-gold" type="button" onClick={startTest}>{t.test.start} <span aria-hidden="true">→</span></button>
            {message && <p className="form-message form-message-error" role="alert">{message}</p>}
          </section>
        )}

        {stage === 'loading' && (
          <div className="test-loading" role="status">
            <span className="loader-crown" aria-hidden="true">♛</span>
            <p>{t.test.loading}</p>
          </div>
        )}

        {stage === 'questions' && currentQuestion && (
          <section className="test-workspace" aria-labelledby="question-title">
            <div className="test-status">
              <div>
                <span>{t.test.question} {currentIndex + 1} {t.test.of} {questions.length}</span>
                <span>{answerCount} / {questions.length} {t.test.answered}</span>
              </div>
              <div className="progress-track" role="progressbar" aria-valuemin={1} aria-valuemax={questions.length} aria-valuenow={currentIndex + 1}>
                <span style={{ width: `${progress}%` }} />
              </div>
            </div>

            <div className="question-card" tabIndex={-1}>
              <div className="question-meta">
                <span>{currentQuestion.category === 'GRAMMAR' ? 'Grammar' : 'Vocabulary'}</span>
                <span>{currentQuestion.difficulty}</span>
              </div>
              <h2 id="question-title">{currentQuestion.prompt}</h2>
              <div className="answer-list" role="radiogroup" aria-labelledby="question-title">
                {currentQuestion.options.map((option, optionIndex) => {
                  const selected = answers[currentQuestion.id] === optionIndex;
                  return (
                    <label className={`answer-option${selected ? ' is-selected' : ''}`} key={option}>
                      <input
                        type="radio"
                        name={`question-${currentQuestion.id}`}
                        aria-label={option}
                        checked={selected}
                        onChange={() => chooseAnswer(optionIndex)}
                      />
                      <span className="answer-letter">{String.fromCharCode(65 + optionIndex)}</span>
                      <span>{option}</span>
                    </label>
                  );
                })}
              </div>
              {message && <p className="form-message form-message-error" role="alert">{message}</p>}
              <div className="question-actions">
                <button className="button button-ghost" type="button" disabled={currentIndex === 0 || isSubmitting} onClick={() => { setCurrentIndex((index) => index - 1); setMessage(''); }}>
                  <span aria-hidden="true">←</span> {t.test.previous}
                </button>
                {currentIndex < questions.length - 1 ? (
                  <button className="button button-gold" type="button" onClick={goNext}>{t.test.next} <span aria-hidden="true">→</span></button>
                ) : (
                  <button className="button button-gold" type="button" disabled={isSubmitting} onClick={submit}>
                    {isSubmitting ? t.test.submitting : t.test.submit} <span aria-hidden="true">→</span>
                  </button>
                )}
              </div>
            </div>
          </section>
        )}

        {stage === 'result' && result && (
          <section className="result-panel" aria-labelledby="result-title">
            <div className="result-summary">
              <p className="eyebrow eyebrow-gold">{t.test.resultTitle}</p>
              <span className="result-label">{t.test.approximate}</span>
              <div className="level-mark" aria-label={`${t.test.approximate} ${result.level}`}>{result.level}</div>
              <h1 id="result-title">{result.score} / {result.total} <small>{t.test.scoreLabel}</small></h1>
              <p>{t.test.resultCopy}</p>
              <button className="text-link result-retake" type="button" onClick={reset}>{t.test.retake} <span aria-hidden="true">↻</span></button>
            </div>
            <div className="contact-card">
              <span className="contact-unlocked"><span aria-hidden="true">◆</span> Queen English Academy</span>
              <h2>{t.test.contactTitle}</h2>
              <p>{t.test.contactBody}</p>
              <div className="teacher-contact-name">{result.teacherName}</div>
              <dl>
                <div><dt>{t.test.phone}</dt><dd><a href={`tel:+${result.teacherPhone.replace(/\D/g, '')}`}>{result.teacherPhone}</a></dd></div>
                <div><dt>{t.test.telegram}</dt><dd><a href={result.bookingUrl} target="_blank" rel="noreferrer">{result.teacherTelegram}</a></dd></div>
              </dl>
              <div className="share-result">
                {wasResultSent ? (
                  <p className="share-result-success" role="status"><span aria-hidden="true">✓</span> {t.test.shareSent}</p>
                ) : (
                  <form className="share-result-form" onSubmit={sendResultToTeacher} noValidate>
                    <h3>{t.test.shareTitle}</h3>
                    <p>{t.test.shareIntro}</p>
                    <div className="test-lead-fields">
                      <label className="test-lead-field">
                        <span>{t.test.nameLabel}</span>
                        <input
                          type="text"
                          name="studentName"
                          autoComplete="name"
                          maxLength={80}
                          placeholder={t.test.namePlaceholder}
                          value={studentName}
                          onChange={(event) => { setStudentName(event.target.value); setShareMessage(''); }}
                          required
                        />
                      </label>
                      <label className="test-lead-field">
                        <span>{t.test.phoneLabel}</span>
                        <input
                          type="tel"
                          name="studentPhone"
                          autoComplete="tel"
                          inputMode="tel"
                          maxLength={30}
                          placeholder={t.test.phonePlaceholder}
                          value={studentPhone}
                          onChange={(event) => { setStudentPhone(event.target.value); setShareMessage(''); }}
                          required
                        />
                      </label>
                    </div>
                    <label className="test-consent">
                      <input
                        type="checkbox"
                        checked={hasContactConsent}
                        onChange={(event) => { setHasContactConsent(event.target.checked); setShareMessage(''); }}
                      />
                      <span>{t.test.consentLabel}</span>
                    </label>
                    <small className="test-privacy-note"><span aria-hidden="true">◆</span> {t.test.secureNote}</small>
                    {shareMessage && <p className="form-message form-message-error" role="alert">{shareMessage}</p>}
                    <div className="share-result-actions">
                      <button className="button button-gold" type="submit" disabled={isSendingResult}>
                        {isSendingResult ? t.test.sending : t.test.shareSubmit} <span aria-hidden="true">→</span>
                      </button>
                    </div>
                  </form>
                )}
              </div>
              <Link className="contact-home-link" to="/">{t.test.home}</Link>
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
